import {
  BuildRequestHeadersOptions,
  executeQuery,
  ExecuteQueryOptions,
  executeQueryWithAutoPagination,
  TypedDocumentNode,
} from '@datocms/cda-client';
import useAsyncMemo from 'hooks/useAsyncMemo';

export const queryOptions: Omit<BuildRequestHeadersOptions, 'token'> = {
  includeDrafts: process.env.NEXT_PUBLIC_CMS_DRAFTS === 'true',
  environment: process.env.NEXT_PUBLIC_CMS_ENV || 'main',
  baseEditingUrl: 'https://cb-cms.admin.datocms.com/',
};

export interface QueryOptions<Variables> extends Omit<
  ExecuteQueryOptions<Variables>,
  'token' | 'baseEditingUrl' | 'variables'
> {
  extendLimit?: boolean;
}

export function query<Result = unknown, Variables = unknown>(
  query: TypedDocumentNode<Result, Variables> | string,
  variables?: Variables,
  options: QueryOptions<Variables> = {},
): Promise<Result> {
  const queryFunction = options.extendLimit ? executeQueryWithAutoPagination : executeQuery;

  return queryFunction(query as TypedDocumentNode<Result, Variables>, {
    token: process.env.NEXT_PUBLIC_CMS_API_KEY!,
    ...options,
    ...queryOptions,
    variables,
  });
}

/** Page size used when walking a CMS collection. Callers keep going until a short page comes back. */
export const CMS_COLLECTION_PAGE_SIZE = 100;

export async function queryEveryPage<T>(fetchPage: (skip: number) => Promise<T[]>): Promise<T[]> {
  const items: T[] = [];
  let skip = 0;

  for (;;) {
    const page = await fetchPage(skip);
    items.push(...page);
    if (page.length < CMS_COLLECTION_PAGE_SIZE) return items;
    skip += CMS_COLLECTION_PAGE_SIZE;
  }
}

export function useQuery<Result = unknown, Variables = unknown>(
  gqlQuery: TypedDocumentNode<Result, Variables> | string,
  variables?: Variables,
  options?: QueryOptions<Variables> & { skip?: boolean },
) {
  return useAsyncMemo(async () => {
    if (options?.skip) return undefined;

    const response = await query(gqlQuery, variables, options);
    return response;
  }, [gqlQuery, variables, options]);
}
